import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { HomeV2Layout, useTimeOfDay } from '../components/home-v2';
import { TipCard } from '../components/expert-tips';
import { useExpertTips } from '../hooks/useExpertTips';
import { useSubmitExpertTipFeedback } from '../hooks/useExpertTipFeedback';
import {
  EXPERT_TIPS_COLORS,
  EXPERT_TIPS_RESPONSIVE,
  EXPERT_TIPS_TYPOGRAPHY,
} from '../components/expert-tips/constants';
import { containerVariants, itemVariants } from '../lib/animations';
import type { FeedbackType } from '../types/expert-tip.types';
import { useContentManager } from './HomeV2Page';
import { CheckInTransitionOverlay, useCheckInTransition } from '../components/home-v2/check-in-transition';

export default function ExpertTipsPage() {
  const { track } = useAnalytics();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useExpertTips(20);

  const {
    // cloudRef,
    isTransitioning,
    cloudRect,
    startTransition,
    onTransitionComplete,
    // resetTransition,
  } = useCheckInTransition();

  const { config } = useTimeOfDay();

  const { mutateAsync: submitFeedback, isPending: isFeedbackSubmitting } =
    useSubmitExpertTipFeedback();

  const [expandedTipId, setExpandedTipId] = useState<number | null>(null);

  useEffect(() => {
    track('expert_tips_view');
  }, [track]);

  const handleToggle = useCallback((tipId: number, tipTitle?: string) => {
    const isExpanding = expandedTipId !== tipId;
    setExpandedTipId((prev) => (prev === tipId ? null : tipId));
    if (isExpanding) {
      track('expert_tip_click', { tip_id: String(tipId), tip_title: tipTitle ?? '' });
    }
  }, [expandedTipId, track]);

  const handleFeedbackSubmit = useCallback(
    async (tipId: number, feedbackType: FeedbackType) => {
      await submitFeedback({ expertTipId: tipId, feedbackType });
    },
    [submitFeedback]
  );

  const allTips = data?.pages.flatMap((page) => page.tips) ?? [];
  const topic = data?.pages[0]?.topic ?? null;

  const {
    hideHeaderAvatar
  } = useContentManager()

  return (
    <HomeV2Layout
      activeTab="expert-tips"
      timeOfDayConfig={config}
      //backgroundGradient="linear-gradient(to bottom, #01A7C8 0%, #01A7C8 20%, #FFFFFF 50%, #FFFFFF 100%)"
      maxWidthClass="max-w-md md:max-w-xl lg:max-w-2xl"
      showNightDecorations={false}
      showMorningGlow={false}
      showCloudLogo
      gradientFunction='expert-tips'
      showAvatar={!hideHeaderAvatar}
      onCloudClick={startTransition}
    >
      {/* Gradient hero + topic title area */}
      <div className="px-2 sm:px-4 md:px-6 pt-6 pb-4">
        <p
          className={EXPERT_TIPS_TYPOGRAPHY.topicLabel}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            color: EXPERT_TIPS_COLORS.white,
            margin: 0,
          }}
        >
          Three small tips this week
        </p>

        <h1
          className={EXPERT_TIPS_TYPOGRAPHY.topicTitle}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            color: EXPERT_TIPS_COLORS.white,
            margin: '8px 0 0 0',
          }}
        >
          {topic?.title ?? 'Expert Tips'}
        </h1>
      </div>

      {/* Tip cards section */}
      <div className="px-2 sm:px-4 md:px-6 pb-8">
        {isLoading && <TipListSkeleton />}

        {isError && (
          <div className="text-center py-16">
            <p
              className="text-base mb-4"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: '#DC2626',
              }}
            >
              {error instanceof Error ? error.message : 'Failed to load expert tips'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 rounded-lg text-white font-semibold"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                backgroundColor: EXPERT_TIPS_COLORS.primaryBlue,
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {!isLoading && !isError && allTips.length === 0 && (
          <div className="text-center py-16">
            <p
              className="text-base"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: EXPERT_TIPS_COLORS.textSecondary,
              }}
            >
              No expert tips available yet. Check back soon!
            </p>
          </div>
        )}

        {!isLoading && !isError && allTips.length > 0 && (
          <motion.div
            className={`flex flex-col ${EXPERT_TIPS_RESPONSIVE.cardGapClasses}`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {allTips.map((tip) => (
              <motion.div key={tip.id} variants={itemVariants}>
                <TipCard
                  tip={tip}
                  isExpanded={expandedTipId === tip.id}
                  onToggle={() => handleToggle(tip.id, tip.title)}
                  onFeedbackSubmit={handleFeedbackSubmit}
                  isFeedbackSubmitting={isFeedbackSubmitting}
                />
              </motion.div>
            ))}

            {hasNextPage && (
              <motion.div variants={itemVariants} className="flex justify-center pt-2">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className={`px-8 py-3 rounded-xl ${EXPERT_TIPS_TYPOGRAPHY.loadMoreButton}`}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    backgroundColor: EXPERT_TIPS_COLORS.gray6,
                    color: EXPERT_TIPS_COLORS.white,
                    opacity: isFetchingNextPage ? 0.6 : 1,
                    cursor: isFetchingNextPage ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {isFetchingNextPage ? 'LOADING...' : 'LOAD MORE'}
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
      {/* Check-in transition overlay */}
      {isTransitioning && (
        <CheckInTransitionOverlay
          cloudRect={cloudRect}
          onComplete={onTransitionComplete}
        // onReplay={resetTransition}
        />
      )}

    </HomeV2Layout>
  );
}

function TipListSkeleton() {
  return (
    <div className={`flex flex-col ${EXPERT_TIPS_RESPONSIVE.cardGapClasses}`}>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl bg-white/60 p-4"
          style={{ height: '114px' }}
        >
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-3 bg-gray-200 rounded w-1/4" />
        </div>
      ))}
    </div>
  );
}
